"use client";

import React from "react";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Scale } from "lucide-react";
const responsibilities = ["Identifying and safeguarding probate assets", "Reviewing financial and property records", "Preparing inventories", "Addressing creditor claims", "Paying authorized estate expenses", "Completing required notices and court filings", "Communicating with beneficiaries and other interested persons", "Distributing probate assets when authorized", "Completing the steps necessary to close the estate"];
export default function ProbateGuidance() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="probateguidance" className="bg-background py-12 md:py-16 lg:py-20 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div ref={headerRef} className={`lg:col-span-5 lg:sticky lg:top-32 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="inline-flex items-center justify-center p-3 bg-primary/5 rounded-lg">
                <Scale className="w-6 h-6 text-primary" />
              </div>

              <div className="inline-flex items-center px-3 py-1 rounded-md border border-secondary/30 text-xs font-medium text-secondary">
                PROBATE ADMINISTRATION
              </div>
            </div>

            <h2 className="text-2xl md:text-3xl lg:text-4xl font-default font-semibold text-foreground tracking-tight mb-4">
              Probate Guidance &amp; Administration Information
            </h2>

            <Separator className="w-12 h-1 bg-secondary mb-4" />

            <p className="text-lg text-muted-foreground leading-relaxed">
              Probate is the legal process of administering a deceased
              person&apos;s estate under Florida law. Depending on the
              circumstances, administration may involve identifying probate
              assets, reviewing governing documents, addressing creditor
              claims, completing required court filings, and distributing
              estate assets in accordance with the Will or applicable law.
            </p>
          </div>

          <div ref={contentRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="mb-8">
              <h3 className="text-2xl md:text-3xl font-default font-medium text-foreground mb-3">
                The Role of the Personal Representative
              </h3>

              <p className="text-muted-foreground leading-relaxed max-w-3xl">In Florida, the Personal Representative is appointed to administer the probate estate. The Law Office of Morrie I. Levine assists Personal Representatives, beneficiaries, trustees, and interested persons in matters involving probate administration.</p>
            </div>

            <div className="mb-8">
              <p className="text-xs md:text-sm font-medium uppercase tracking-[0.14em] text-secondary mb-4">
                Responsibilities May Include
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                {responsibilities.map(responsibility => <div key={responsibility} className="flex items-start gap-3 border-t border-border py-4">
                    <span aria-hidden="true" className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />

                    <p className="text-muted-foreground leading-relaxed">
                      {responsibility}
                    </p>
                  </div>)}
              </div>
            </div>

            <div className="border-l-2 border-secondary/40 pl-5">
              <p className="text-sm text-muted-foreground leading-relaxed italic">
                The specific responsibilities depend upon the governing
                documents, the nature of the estate, applicable Florida law,
                and court orders.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}